import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useToast } from "../../../hooks/toast";
import api from "../../../service";
import { useCallback } from "react";

import {
  FormContainer,
  DefaulterWarning,
  FormText,
  FormTextContainer,
} from "./styles";
import { useLoading } from "../../../hooks/loading";
import printJS from "print-js";
import { base64ToArrayBuffer, mergePdfs } from "../../../utils/file";
import generateHeaders from "../../../utils/generateHeaders";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { ModalButtonsWrapper } from "../../Defaulters/styles";
import Button from "../../Defaulters/components/button";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FormRow } from "../../../components/ControlledInputs/styles";
import ControlledInput from "../../../components/ControlledInputs/input";
import ControlledTextareaInput from "../../../components/ControlledInputs/input-textarea";

const getSchema = (isDefaulter) => {
  const baseSchema = {
    company_name_credential: z.string().min(1, {
      message: "error.field_required",
    }),
  };

  if (isDefaulter) {
    return z.object({
      ...baseSchema,
      reason: z
        .string()
        .min(1, {
          message: "error.field_required",
        })
        .refine((data) => {
          return data.trim().length > 1;
        }),
    });
  }

  return z.object(baseSchema);
};

const PrintCompanyNameModal = ({
  open,
  setOpen,
  credentialsId,
  originalCredentialCompanyName,
  updateCallback,
  defaulter = false,
}) => {
  const [isDefaulter, setIsDefaulter] = useState(defaulter);
  const { token } = useSelector((state) => state.auth);
  const intl = useIntl();
  const { addToast } = useToast();
  const { handleLoading } = useLoading();

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(getSchema(defaulter)),
    defaultValues: {
      company_name_credential: originalCredentialCompanyName,
    },
  });

  const handlePrint = useCallback(
    async (values) => {
      try {
        const options = generateHeaders(token);

        if (values?.reason) {
          await api.post(
            "/admin/accreditation/print-override",
            {
              credentialIds: credentialsId,
              reason: values.reason,
            },
            options
          );
        }

        handleLoading(true);

        const response = await api.patch(
          "/credential-reports",
          {
            credentialsId,
            credentialCompanyName: values.company_name_credential,
            channel: "ADMIN",
          },
          options
        );

        const { data } = response;
        const filteredOutput = data.reportOutputList.filter((value) => value);

        if (filteredOutput.length <= 0) {
          return;
        }

        let arrayBuffers = filteredOutput.map((apiResult) => {
          return base64ToArrayBuffer(apiResult.file);
        });

        const base64String = btoa(
          [].reduce.call(
            new Uint8Array(await mergePdfs(arrayBuffers)),
            function (p, c) {
              return p + String.fromCharCode(c);
            },
            ""
          )
        );

        updateCallback();

        printJS({
          printable: base64String,
          type: "pdf",
          base64: true,
        });

        setOpen(false);
      } catch (err) {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: `api.error.${err.response?.data?.code}`,
          }),
        });
      } finally {
        handleLoading(false);
      }
    },
    [
      token,
      handleLoading,
      credentialsId,
      updateCallback,
      setOpen,
      addToast,
      intl,
    ]
  );

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      centered={true}
      closable={false}
      width={600}
      footer={""}
      className="modalLesson"
      destroyOnClose
    >
      {!isDefaulter && (
        <FormContainer onSubmit={handleSubmit(handlePrint)}>
          <FormTextContainer>
            {/* <FormText>
              {intl.formatMessage(
                { id: "company_name_information_part_1" },
                { companyName: originalCredentialCompanyName || "-" }
              )}
            </FormText> */}
            <FormText>
              {intl.formatMessage({ id: "company_name_information_part_2" })}
            </FormText>
          </FormTextContainer>
          <FormRow marginBottom={"24px"}>
            <ControlledInput
              name={"company_name_credential"}
              control={control}
              formState={formState}
              label={"company_name_credential"}
              required={true}
            />
          </FormRow>
          {defaulter && (
            <FormRow marginBottom={"24px"}>
              <ControlledTextareaInput
                name={"reason"}
                control={control}
                formState={formState}
                label={"provide_reason"}
                required={true}
                min={1}
                max={300}
              />
            </FormRow>
          )}
          <ModalButtonsWrapper width="100%">
            <Button
              type="button"
              variant="secondary"
              label={"close"}
              shadow
              onClick={() => setOpen(false)}
            />
            <Button label={"print"} shadow />
          </ModalButtonsWrapper>
        </FormContainer>
      )}

      {isDefaulter && (
        <DefaulterWarning>
          <ExclamationCircleOutlined
            style={{
              width: "64px",
              height: "64px",
              color: "#002244",
            }}
          />
          <p>
            <FormattedMessage id="credential_locked" />
          </p>
          <ModalButtonsWrapper width="100%">
            <Button
              variant="secondary"
              label={"close"}
              shadow
              onClick={() => setOpen(false)}
            />
            <Button
              label={"open_exception"}
              shadow
              onClick={() => setIsDefaulter(false)}
            />
          </ModalButtonsWrapper>
        </DefaulterWarning>
      )}
    </Modal>
  );
};

export default PrintCompanyNameModal;
